import { useCallback, useRef } from 'react'
import { Card } from '../../atoms'
import { MapControls, Compass } from '../../molecules'
import type { MapContainerProps, POIElement, PinnedElement } from './types'
import { useAppStore } from '../../../store/appStore'
import {
  useMapInitialization,
  useMapTiles,
  useMapSnapshot,
  useMapInteractions,
  useOverlayPositions,
  useMapRotation,
  useUserMarker,
  usePOIMarkers,
  usePinnedLocations,
  useMapFollow,
  usePinMode,
  useMapViewPersistence,
} from './hooks'

export const MapContainer = ({
  position,
  heading,
  mapMode,
  follow,
  accuracy,
  onMapModeToggle,
  onZoomIn,
  onZoomOut,
  poiPosition,
  nearbyPOIs = [],
  onMapReady,
  mapType = 'osm',
  pinnedLocations = [],
  onMapClick,
  pinMode = false,
  compassLocked = false,
  onCompassLockToggle,
  manualRotation = null,
  onManualRotation,
  onPOIMarkerClick,
  isTracking = false,
}: MapContainerProps) => {
  const poiOverlayRef = useRef<HTMLDivElement | null>(null)
  
  // Get saved map view from store
  const savedMapView = useAppStore((state) => state.savedMapView)
  const setSavedMapView = useAppStore((state) => state.setSavedMapView)

  // Initialize map
  const {
    mapRef,
    mapContainerRef,
    tileLayerRef,
    isInitializedRef,
    isLoadingTiles,
    setIsLoadingTiles,
    tilesLoadingCountRef,
    loadingTimeoutRef,
  } = useMapInitialization({
    mapType,
    position,
    savedMapView,
    onMapReady,
    onMapClick,
  })

  // Map snapshot management
  const { isSnapshotLockedRef, saveMapSnapshot } = useMapSnapshot({
    mapRef,
    isInitializedRef,
  })

  // Track user interactions
  const { userHasInteractedRef, isUserDraggingRef } = useMapInteractions({
    mapRef,
    isInitializedRef,
    saveMapSnapshot,
  })

  // Handle tile layer switching
  useMapTiles({
    mapRef,
    tileLayerRef,
    isInitializedRef,
    mapType,
    tilesLoadingCountRef,
    setIsLoadingTiles,
    isLoadingTiles,
    loadingTimeoutRef,
    userHasInteractedRef,
  })

  // Overlay positioning system
  const poiElementsRef = useRef<Map<string, POIElement>>(new Map())
  const pinnedElementsRef = useRef<Map<string, PinnedElement>>(new Map())
  const userMarkerElementRef = useRef<HTMLDivElement | null>(null)

  const { updateOverlayPositions } = useOverlayPositions({
    mapRef,
    poiOverlayRef,
    poiElementsRef,
    pinnedElementsRef,
    userMarkerElementRef,
    isInitializedRef,
    position,
  })

  // Map rotation
  const { mapRotRef } = useMapRotation({
    mapRef,
    isInitializedRef,
    mapMode,
    heading,
    compassLocked,
    manualRotation,
  })

  // User marker
  useUserMarker({
    mapRef,
    poiOverlayRef,
    isInitializedRef,
    position,
    accuracy,
    updateOverlayPositions,
    userMarkerElementRef,
  })

  // POI markers
  usePOIMarkers({
    mapRef,
    poiOverlayRef,
    isInitializedRef,
    nearbyPOIs,
    poiPosition,
    onPOIMarkerClick,
    updateOverlayPositions,
    poiElementsRef,
  })

  // Pinned locations
  usePinnedLocations({
    mapRef,
    poiOverlayRef,
    isInitializedRef,
    pinnedLocations,
    updateOverlayPositions,
    pinnedElementsRef,
  })

  // Follow mode
  useMapFollow({
    mapRef,
    isInitializedRef,
    position,
    follow,
    isTracking,
    userHasInteractedRef,
    isUserDraggingRef,
    isSnapshotLockedRef,
    saveMapSnapshot,
    setIsLoadingTiles,
  })

  // Pin mode
  usePinMode({
    mapRef,
    isInitializedRef,
    pinMode,
    onMapClick,
  })

  // Save map view to localStorage
  useMapViewPersistence({
    mapRef,
    isInitializedRef,
    onSaveView: setSavedMapView,
  })

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.zoomIn()
      onZoomIn()
    }
  }, [onZoomIn, mapRef])

  const handleZoomOut = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.zoomOut()
      onZoomOut()
    }
  }, [onZoomOut, mapRef])

  return (
    <Card className="p-0 overflow-hidden">
      <div
        className="relative h-[400px] sm:h-[450px] md:h-[500px] lg:h-[550px] rounded-xl overflow-hidden bg-background"
        role="application"
        aria-label="Interactive map showing your location and points of interest"
      >
        {/* Rotation wrapper */}
        <div
          ref={mapRotRef}
          className="absolute inset-0 origin-center transition-transform duration-300"
          style={{ transformOrigin: '50% 50%' }}
        >
          {/* Map container */}
          <div
            ref={mapContainerRef}
            className="absolute inset-0 w-full h-full"
            aria-label="Map view"
          />
        </div>

        {/* POI Overlay - manually positioned layer on top of map */}
        <div
          ref={poiOverlayRef}
          className="absolute inset-0 pointer-events-none z-[900]"
          style={{ pointerEvents: 'none' }}
        >
          {/* POI markers will be added here as children */}
        </div>

        {/* Overlay controls */}
        <Compass
          heading={heading}
          mapMode={mapMode}
          onMapModeToggle={onMapModeToggle}
          onManualRotation={onManualRotation}
          manualRotation={manualRotation}
          isLocked={compassLocked}
          onLockToggle={onCompassLockToggle}
        />

        <MapControls onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} />

        {/* Loading overlay - only show during initial load and map type changes */}
        {isLoadingTiles && (
          <div
            className="absolute inset-0 flex items-center justify-center z-[1000] pointer-events-none"
            role="status"
            aria-label="Loading map tiles"
          >
            <p className="text-xs sm:text-sm text-text font-medium bg-background/90 px-3 sm:px-4 py-1.5 sm:py-2 rounded-md shadow-lg">
              Loading map tiles...
            </p>
          </div>
        )}
      </div>
    </Card>
  )
}

export type { MapContainerProps } from './types'
